import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { Plus, RotateCcw, RotateCw } from 'lucide-react'
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'

import { FormCapsLabel } from '@/components/ui/form-caps-label'
import {
  blobToDataUrl,
  compressImageToJpeg,
  cropImageRegion,
  type CropRegion,
  rotateImageQuarterTurn,
} from '@/lib/imageCompression'

export interface PhotoNoteSubmit {
  /** The group's images, display order. A single-photo note has one entry. */
  photoFiles: File[]
  caption: string
  /** Data URLs matching photoFiles, for optimistic display. */
  photoUrls: string[]
}

export interface PhotoNoteReplace {
  photoFile: File
  photoUrl: string
}

export interface PhotoNoteComposerHandle {
  /** Open the file picker to add a new photo note. */
  startAdd: () => void
  /** Open the file picker to replace an existing photo note's image. */
  startReplace: (noteId: string) => void
  /**
   * Open the crop/rotate card pre-loaded with a note's existing image (no
   * file picker). Falls back to the file picker if the image cannot be
   * fetched. Saving replaces the note's photo like startReplace.
   */
  startAdjust: (noteId: string, photoUrl: string) => void
}

export interface PhotoNoteComposerProps {
  /** Create a new photo note. Receives the cropped, compressed image. */
  onSubmitPhoto: (input: PhotoNoteSubmit) => void
  /** Replace the image of an existing photo note. */
  onReplacePhoto?: (noteId: string, input: PhotoNoteReplace) => void
  /** Error to surface (e.g. from a failed save). */
  error?: string | null
  /** Clear the current error (called when starting a new photo). */
  onClearError?: () => void
  /** Notified when the pending-photo card opens/closes (e.g. so a host
      panel can toggle scrolling). */
  onOpenChange?: (open: boolean) => void
}

interface PendingPhoto {
  file: File
  dataUrl: string
}

const dataUrlMimeType = (value: string) => {
  const match = /^data:([^;]+);/i.exec(value)
  return match?.[1]
}

const filenameMimeType = (filename: string) => {
  const lastDot = filename.lastIndexOf('.')
  if (lastDot <= 0 || lastDot === filename.length - 1) return undefined
  const ext = filename.slice(lastDot + 1).toLowerCase()
  const mimeMap: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp',
    tif: 'image/tiff',
    tiff: 'image/tiff',
    heic: 'image/heic',
    heif: 'image/heif',
    avif: 'image/avif',
    svg: 'image/svg+xml',
  }
  return mimeMap[ext]
}

const isImageMime = (value?: string) => Boolean(value?.startsWith('image/'))

const normalizePhotoFile = (file: File, dataUrl?: string) => {
  if (isImageMime(file.type)) return file
  const inferredType = dataUrl ? dataUrlMimeType(dataUrl) : undefined
  const extensionType = filenameMimeType(file.name)
  const type = isImageMime(inferredType)
    ? inferredType
    : isImageMime(extensionType)
      ? extensionType
      : undefined
  if (!type) return file
  try {
    return new File([file], file.name || 'photo.jpg', { type })
  } catch {
    return file
  }
}

/** Map a crop selection (rendered pixels) to natural image pixels. */
const cropToNaturalRegion = (
  img: HTMLImageElement | null,
  crop: PixelCrop | undefined,
): CropRegion | undefined => {
  if (!img || !crop || crop.width < 1 || crop.height < 1) return undefined
  const scaleX = img.naturalWidth / img.width
  const scaleY = img.naturalHeight / img.height
  return {
    x: crop.x * scaleX,
    y: crop.y * scaleY,
    width: crop.width * scaleX,
    height: crop.height * scaleY,
  }
}

/**
 * Self-contained photo-note capture: file picker, per-photo drag-to-crop
 * (including across a multi-photo pick), and client-side JPEG compression,
 * shared by Log Work and the service details Notes panel. Triggered
 * imperatively (startAdd / startReplace); renders the hidden file input plus
 * the pending crop/caption card.
 */
export const PhotoNoteComposer = forwardRef<
  PhotoNoteComposerHandle,
  PhotoNoteComposerProps
>(function PhotoNoteComposer(props, ref) {
  const { onSubmitPhoto, onReplacePhoto, error, onClearError, onOpenChange } =
    props

  const [isAddingPhoto, setIsAddingPhoto] = useState(false)
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([])
  const [pendingPhotoCaption, setPendingPhotoCaption] = useState('')
  const [photoMode, setPhotoMode] = useState<'new' | 'edit' | null>(null)
  const [editingPhotoNoteId, setEditingPhotoNoteId] = useState<string | null>(null)
  // Which pending photo is open in the crop editor (multi-photo pick only —
  // a lone photo is always the active one). null means the thumbnail strip.
  const [editingPhotoIndex, setEditingPhotoIndex] = useState<number | null>(null)
  const [activeCrop, setActiveCrop] = useState<Crop | undefined>()
  const [activeCompletedCrop, setActiveCompletedCrop] = useState<PixelCrop | undefined>()
  const [isProcessingPhoto, setIsProcessingPhoto] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const cropImgRef = useRef<HTMLImageElement | null>(null)

  const resetPendingCrop = () => {
    setActiveCrop(undefined)
    setActiveCompletedCrop(undefined)
  }

  const resetForPhoto = (mode: 'new' | 'edit', noteId?: string) => {
    onClearError?.()
    setPhotoMode(mode)
    setEditingPhotoNoteId(noteId ?? null)
    setIsAddingPhoto(true)
    onOpenChange?.(true)
    setPendingPhotos([])
    setPendingPhotoCaption('')
    setEditingPhotoIndex(null)
    resetPendingCrop()
  }

  const beginPhoto = (mode: 'new' | 'edit', noteId?: string) => {
    resetForPhoto(mode, noteId)
    fileInputRef.current?.click()
  }

  /** Load a note's existing image into the pending card for crop/rotate. */
  const beginAdjust = async (noteId: string, photoUrl: string) => {
    resetForPhoto('edit', noteId)
    try {
      const response = await fetch(photoUrl)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const blob = await response.blob()
      const file = new File([blob], 'photo.jpg', {
        type: blob.type || 'image/jpeg',
      })
      const dataUrl = await blobToDataUrl(blob)
      setPendingPhotos([{ file: normalizePhotoFile(file, dataUrl), dataUrl }])
    } catch {
      // Existing image unavailable (deleted file, network) — the card is
      // already open, so let the user pick a replacement instead.
      fileInputRef.current?.click()
    }
  }

  useImperativeHandle(ref, () => ({
    startAdd: () => beginPhoto('new'),
    startReplace: (noteId: string) => beginPhoto('edit', noteId),
    startAdjust: (noteId: string, photoUrl: string) => {
      void beginAdjust(noteId, photoUrl)
    },
  }))

  const handleCancelPhoto = () => {
    onClearError?.()
    setIsAddingPhoto(false)
    onOpenChange?.(false)
    setPendingPhotos([])
    setPendingPhotoCaption('')
    setPhotoMode(null)
    setEditingPhotoNoteId(null)
    setEditingPhotoIndex(null)
    resetPendingCrop()
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // A lone pending photo goes straight into the crop editor; a multi-photo
  // pick shows the thumbnail strip until a specific photo is tapped.
  const activeIndex = pendingPhotos.length === 1 ? 0 : editingPhotoIndex
  const activePhoto = activeIndex !== null ? pendingPhotos[activeIndex] : null

  const openCropEditor = (index: number) => {
    setEditingPhotoIndex(index)
    resetPendingCrop()
  }

  const closeCropEditor = () => {
    setEditingPhotoIndex(null)
    resetPendingCrop()
  }

  /** Discard the in-progress crop selection without applying it. */
  const clearActiveCrop = () => {
    resetPendingCrop()
  }

  /**
   * Bake the in-progress crop selection into the active photo, like rotate
   * already does — a deliberate "lock it in" action, not a live preview, so
   * it only fires when tapped rather than on every drag adjustment.
   */
  const handleLockInCrop = async () => {
    if (activeIndex === null || !activePhoto || isProcessingPhoto) return
    const region = cropToNaturalRegion(cropImgRef.current, activeCompletedCrop)
    if (!region) return
    const index = activeIndex
    setIsProcessingPhoto(true)
    try {
      const cropped = await cropImageRegion(activePhoto.file, region)
      if (cropped) {
        setPendingPhotos((prev) =>
          prev.map((p, i) =>
            i === index ? { file: cropped.file, dataUrl: cropped.dataUrl } : p,
          ),
        )
        resetPendingCrop()
      }
    } finally {
      setIsProcessingPhoto(false)
    }
  }

  /** Rotate the active photo a quarter turn; any crop selection resets. */
  const handleRotatePhoto = async (direction: 1 | -1) => {
    if (activeIndex === null || !activePhoto || isProcessingPhoto) return
    const index = activeIndex
    setIsProcessingPhoto(true)
    try {
      const rotated = await rotateImageQuarterTurn(activePhoto.file, direction)
      if (rotated) {
        setPendingPhotos((prev) =>
          prev.map((p, i) => (i === index ? { file: rotated.file, dataUrl: rotated.dataUrl } : p)),
        )
        resetPendingCrop()
      }
    } finally {
      setIsProcessingPhoto(false)
    }
  }

  // The photos are the note. A caption adds context but was never the point,
  // so only the images themselves gate saving.
  const canSavePhoto = pendingPhotos.length > 0 && !isProcessingPhoto

  const handleSavePhotoNote = async () => {
    const caption = pendingPhotoCaption.trim()
    if (pendingPhotos.length === 0 || isProcessingPhoto) return

    // Crop/rotate are already baked into pending.file by this point — just
    // re-encode each image down to the note photo budget before upload.
    // Compression failing (e.g. an undecodable format on this browser) falls
    // back to the original file.
    setIsProcessingPhoto(true)
    const photoFiles: File[] = []
    const photoUrls: string[] = []
    try {
      for (const pending of pendingPhotos) {
        const compressed = await compressImageToJpeg(pending.file)
        photoFiles.push(compressed?.file ?? pending.file)
        photoUrls.push(compressed?.dataUrl ?? pending.dataUrl)
      }
    } finally {
      setIsProcessingPhoto(false)
    }

    if (photoMode === 'edit' && editingPhotoNoteId) {
      onReplacePhoto?.(editingPhotoNoteId, {
        photoFile: photoFiles[0],
        photoUrl: photoUrls[0],
      })
      handleCancelPhoto()
      return
    }

    onSubmitPhoto({ photoFiles, caption, photoUrls })
    handleCancelPhoto()
  }

  const hasFreshSelection = Boolean(
    activeCompletedCrop && activeCompletedCrop.width >= 1,
  )

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={photoMode !== 'edit'}
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? [])
          if (files.length === 0) return
          // Replacing targets exactly one image; adding accepts a whole pick
          // and appends to whatever is already pending.
          const accepted = photoMode === 'edit' ? files.slice(0, 1) : files
          void Promise.all(
            accepted.map(
              (file) =>
                new Promise<{ file: File; dataUrl: string } | null>(
                  (resolve) => {
                    const reader = new FileReader()
                    reader.onload = () => {
                      const result = reader.result
                      resolve(
                        typeof result === 'string'
                          ? { file: normalizePhotoFile(file, result), dataUrl: result }
                          : null,
                      )
                    }
                    reader.onerror = () => resolve(null)
                    reader.readAsDataURL(file)
                  },
                ),
            ),
          ).then((loaded) => {
            const photos = loaded.filter(
              (entry): entry is { file: File; dataUrl: string } =>
                Boolean(entry),
            )
            if (photos.length === 0) return
            // New photos append after whatever is already pending. Picking
            // exactly one photo (the very first, or one more added to an
            // existing set) jumps straight into cropping/editing it; picking
            // several at once lands on the thumbnail strip instead.
            const nextPhotos =
              photoMode === 'edit' ? photos : [...pendingPhotos, ...photos]
            setPendingPhotos(nextPhotos)
            setEditingPhotoIndex(photos.length === 1 ? nextPhotos.length - 1 : null)
            resetPendingCrop()
          })
          // Allow re-picking the same file(s) later.
          event.target.value = ''
        }}
      />

      {isAddingPhoto ? (
        <div className="space-y-3 text-xs">
          {error ? (
            <p role="alert" className="text-xs whitespace-pre-line text-red-600">
              {error}
            </p>
          ) : null}
          <FormCapsLabel>
            {photoMode === 'edit' ? 'Edit photo' : 'New photo note'}
          </FormCapsLabel>
          {activeIndex !== null && activePhoto ? (
            <div className="space-y-1.5">
              {pendingPhotos.length > 1 ? (
                <div className="text-[11px] font-medium text-slate-500">
                  Editing photo {activeIndex + 1} of {pendingPhotos.length}
                </div>
              ) : null}
              <div className="flex justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-100">
                {/* Shrink-wraps to the rendered image (a flex item sizes to
                    its content), so the overlay below lines up with the
                    photo's actual edges rather than this row's full width. */}
                <div className="relative">
                  <ReactCrop
                    crop={activeCrop}
                    onChange={(crop) => setActiveCrop(crop)}
                    onComplete={(crop) => setActiveCompletedCrop(crop)}
                    className="max-h-[60vh]"
                  >
                    {/* react-image-crop measures this element to map the
                        selection, so it must size to the visible image
                        (no object-contain letterboxing). */}
                    <img
                      ref={cropImgRef}
                      src={activePhoto.dataUrl}
                      alt="User uploaded"
                      className="block max-h-[60vh] w-auto max-w-full"
                    />
                  </ReactCrop>
                  {/* Floating toolbar, overlapping the photo for bigger
                      touch targets without pushing the layout around. */}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-2">
                    <div className="pointer-events-auto flex gap-2">
                      <button
                        type="button"
                        className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-slate-900/70 text-white hover:bg-slate-900 disabled:opacity-50"
                        aria-label="Rotate left"
                        title="Rotate left"
                        disabled={isProcessingPhoto}
                        onClick={() => handleRotatePhoto(-1)}
                      >
                        <RotateCcw className="h-5 w-5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-slate-900/70 text-white hover:bg-slate-900 disabled:opacity-50"
                        aria-label="Rotate right"
                        title="Rotate right"
                        disabled={isProcessingPhoto}
                        onClick={() => handleRotatePhoto(1)}
                      >
                        <RotateCw className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                    {hasFreshSelection ? (
                      <div className="pointer-events-auto flex gap-2">
                        <button
                          type="button"
                          className="h-11 cursor-pointer rounded-full bg-slate-900/70 px-4 text-xs font-medium text-white hover:bg-slate-900"
                          onClick={clearActiveCrop}
                        >
                          Clear
                        </button>
                        <button
                          type="button"
                          className="h-11 cursor-pointer rounded-full bg-blue-600 px-5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                          disabled={isProcessingPhoto}
                          onClick={handleLockInCrop}
                        >
                          Crop
                        </button>
                      </div>
                    ) : pendingPhotos.length > 1 ? (
                      // No selection in progress — this corner is free, so
                      // "done with this photo" lives here instead of a big
                      // bar below the editor.
                      <button
                        type="button"
                        className="pointer-events-auto h-11 cursor-pointer rounded-full bg-blue-600 px-5 text-xs font-semibold text-white hover:bg-blue-700"
                        onClick={closeCropEditor}
                      >
                        Confirm
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between text-[11px] text-slate-500">
                <span>
                  {hasFreshSelection
                    ? 'Tap Crop to apply the selection.'
                    : 'Drag on the photo to crop (optional).'}
                </span>
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {/* In new mode the picker appends, growing the group;
                      replacing stays single-image. */}
                  {photoMode === 'edit' ? (
                    'Replace photo'
                  ) : (
                    <>
                      <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                      Add more photos
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : pendingPhotos.length > 1 ? (
            <div className="space-y-1.5">
              <div className="flex flex-wrap gap-1.5">
                {pendingPhotos.map((pending, index) => (
                  <div
                    key={`${pending.file.name}-${index}`}
                    className="relative overflow-hidden rounded-md border border-slate-200 bg-slate-100"
                  >
                    <button
                      type="button"
                      className="block cursor-pointer"
                      aria-label={`Crop photo ${index + 1}`}
                      onClick={() => openCropEditor(index)}
                    >
                      <img
                        src={pending.dataUrl}
                        alt={`Upload ${index + 1}`}
                        className="h-24 w-24 object-cover"
                      />
                    </button>
                    <button
                      type="button"
                      className="absolute right-1 top-1 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-slate-900/70 text-[11px] leading-none text-white hover:bg-slate-900"
                      aria-label={`Remove photo ${index + 1}`}
                      onClick={() =>
                        setPendingPhotos((prev) =>
                          prev.filter((_, i) => i !== index),
                        )
                      }
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between text-[11px] text-slate-500">
                <span>
                  {pendingPhotos.length} photos — tap one to crop, one note & caption.
                </span>
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  Add more photos
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="flex h-40 w-full cursor-pointer items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50 text-[11px] font-medium tracking-wide text-slate-600 uppercase"
              onClick={() => fileInputRef.current?.click()}
            >
              Tap to upload photos
            </button>
          )}

          {photoMode !== 'edit' ? (
            <input
              value={pendingPhotoCaption}
              placeholder="Add a note…"
              aria-label="Photo caption"
              onChange={(event) => setPendingPhotoCaption(event.target.value)}
              className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2.5 text-[12.5px] text-slate-900"
            />
          ) : null}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              className="inline-flex flex-1 cursor-pointer items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
              onClick={handleCancelPhoto}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex flex-1 cursor-pointer items-center justify-center rounded-md bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!canSavePhoto}
              onClick={handleSavePhotoNote}
            >
              {isProcessingPhoto
                ? pendingPhotos.length > 1
                  ? 'Preparing photos…'
                  : 'Preparing photo…'
                : photoMode === 'edit'
                  ? 'Save photo'
                  : 'Save photo note'}
            </button>
          </div>
        </div>
      ) : null}
    </>
  )
})
